import { jsx as _jsx } from "react/jsx-runtime";
import { BKJTextInput } from '../../BKJTextInput/index.js';
import { mergeRefs } from '../../BKJSelect/utils/mergeRefs.js';
import { useRightIconProps } from '../BKJComboBox.utils.js';
import { useBKJComboBoxContext } from './BKJComboBox.context.js';
import { getComboBoxFocusBorderStyles } from '../BKJComboBox.styles.js';
export const BKJComboBoxInput = ({ className, autoComplete, overrides, showFullFocusBorder = true, ariaLabelledBy, inputRef, autoFocus, ...rest }) => {
    const { label, variant = 'ComboBoxSeamlessRounded', action, error, setAnchorElement, getInputProps, inputValue, getLabelProps, getMenuProps, getComboboxProps, isOpen, onInputFocus, selectedItem, name, leftIconProps, disabled, placeholder, required, isDropdownReverse, } = useBKJComboBoxContext();
    const isLabelValueHidden = overrides.includes('HIDE_VALUE_LABEL');
    const rightIconProps = useRightIconProps(action, disabled);
    // WEB-4811 / R1 / FR-001 + FR-004: Downshift 6.1.12 only emits
    // role="combobox", aria-expanded, aria-haspopup via getComboboxProps()
    // on a wrapper (legacy 1.1). Per the spec we want the 1.2 pattern with
    // these attributes on the input itself. Pass them through getInputProps
    // so they land on the <input> emitted by BKJTextInput's pass-through of
    // ...restInputProps (BKJTextInput.tsx:168).
    //
    // We read the listbox id from getMenuProps() so aria-controls matches
    // the id the listbox actually renders with (they share the same
    // Downshift id counter, but we read it explicitly for clarity).
    const menuId = getMenuProps({}, { suppressRefError: true }).id;
    // WEB-4811 / FR-005 / R11: no custom onKeyDown — Downshift's built-in
    // handlers (ArrowUp/Down, Home/End, Enter selects + closes, Escape
    // closes) match the WAI-ARIA APG editable-combobox pattern. The prior
    // handler set preventDownshiftDefault on Enter/Escape and suppressed
    // those behaviours.
    const inputProps = getInputProps({
        ...rest,
        value: !isOpen && !isLabelValueHidden ? selectedItem?.label || '' : inputValue,
        onFocus: onInputFocus,
        ref: mergeRefs([setAnchorElement, inputRef]),
        placeholder,
        required,
        name,
        label,
        disabled,
        role: 'combobox',
        'aria-haspopup': 'listbox',
        'aria-expanded': isOpen,
        'aria-controls': menuId,
        'aria-autocomplete': 'list',
        ...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {}),
    });
    // WEB-4811 / R5 follow-up: getComboboxProps() is still called — its ref
    // registers the fieldset with Downshift's mouse-and-touch tracker, which
    // drives the "click outside closes" contract (without the ref, a click on
    // the input itself is treated as "outside" and the menu closes
    // immediately). We explicitly override the ARIA attributes to undefined
    // because FR-001 puts role=combobox / aria-haspopup / aria-expanded on
    // the input, not on the fieldset wrapper (ARIA 1.2 pattern). Net effect:
    // ref stays, ARIA doesn't duplicate.
    const fieldSetRefProps = getComboboxProps({
        role: undefined,
        'aria-haspopup': undefined,
        'aria-owns': undefined,
        'aria-expanded': undefined,
    });
    return (_jsx(BKJTextInput, { isOpen: isOpen, labelProps: getLabelProps(), fieldSetProps: fieldSetRefProps, variant: variant, ...inputProps, value: inputProps.value, autoComplete: autoComplete, autoFocus: autoFocus, className: `${inputProps.class} ${className}`, overrides: ['NO_REQUIRED'], rightIconProps: rightIconProps, leftIconProps: leftIconProps, error: isOpen ? false : error, css: isOpen && getComboBoxFocusBorderStyles({ showFullFocusBorder, isDropdownReverse }), useFocusBorder: false }));
};
//# sourceMappingURL=BKJComboBoxInput.js.map